/* =====================================================================
   CASCADIA OVERLAND — shared behavior
   File: assets/cascadia.js

   Load once in layout/theme.liquid, inside <head>:
     <script src="{{ 'cascadia.js' | asset_url }}" defer></script>

   Handles what used to be duplicated in every section:
     1. --co-vw, the viewport width minus the scrollbar
     2. Hero parallax
     3. Scroll reveal for [data-reveal]
     4. Pausing background video when it scrolls out of view
     5. Carousel arrows

   Everything is opt-in by class or attribute, so a page with none of
   these costs nothing.
   ===================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. Viewport width without the scrollbar
     Feeds .co-bleed-out. clientWidth excludes the scrollbar; plain
     100vw does not, which gives Windows users a horizontal scrollbar
     and a few pixels of sideways drift.
     ------------------------------------------------------------------ */
  function setVW() {
    document.documentElement.style.setProperty(
      '--co-vw', document.documentElement.clientWidth + 'px'
    );
  }
  setVW();
  window.addEventListener('resize', setVW);
  window.addEventListener('orientationchange', setVW);

  /* ------------------------------------------------------------------
     2 & 4. Hero parallax and background video
     ------------------------------------------------------------------ */
  function initHeroes() {
    var heroes = document.querySelectorAll('.co-hero');
    if (!heroes.length) return;

    Array.prototype.forEach.call(heroes, function (hero) {
      var layer = hero.querySelector('.co-hero__parallax');
      var video = hero.querySelector('video');

      /* Reduced motion: hold a static frame, skip the parallax */
      if (reduceMotion) {
        if (video) { video.removeAttribute('autoplay'); video.pause(); }
        return;
      }

      if (layer) {
        var ticking = false;
        var FACTOR = 0.3; /* lower is subtler */

        var update = function () {
          var rect = hero.getBoundingClientRect();

          /* Only compute while the hero is near the viewport */
          if (rect.bottom > -200 && rect.top < window.innerHeight + 200) {
            var offset = -window.scrollY * FACTOR;

            /* Clamp to the 10% overscan so the backdrop never shows */
            var max = hero.offsetHeight * 0.1;
            offset = Math.max(-max, Math.min(max, offset));

            layer.style.transform = 'translateY(' + offset + 'px)';
          }
          ticking = false;
        };

        var onScroll = function () {
          if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update();
      }

      /* Pause background video when the hero scrolls away. A looping
         video decodes continuously otherwise, which costs battery on
         mobile for something nobody is looking at.                 */
      if (video && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              var attempt = video.play();
              if (attempt && attempt.catch) { attempt.catch(function () {}); }
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.01 }).observe(hero);
      }
    });
  }

  /* ------------------------------------------------------------------
     3. Scroll reveal
     ------------------------------------------------------------------ */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || reduceMotion) {
      Array.prototype.forEach.call(items, function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    Array.prototype.forEach.call(items, function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     5. Carousel arrows
     Markup: .co-carousel-wrap containing .co-carousel and two
     .co-carousel-btn buttons. Arrows disable at each end so they do
     not look broken when there is nowhere left to scroll.
     ------------------------------------------------------------------ */
  function initCarousels() {
    var wraps = document.querySelectorAll('.co-carousel-wrap');
    if (!wraps.length) return;

    Array.prototype.forEach.call(wraps, function (wrap) {
      var rail = wrap.querySelector('.co-carousel');
      var prev = wrap.querySelector('.co-carousel-btn--prev');
      var next = wrap.querySelector('.co-carousel-btn--next');
      if (!rail) return;

      var step = function () {
        var first = rail.firstElementChild;
        if (!first) return 420;
        var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 14;
        return (first.getBoundingClientRect().width + gap) * 2;
      };

      var sync = function () {
        var maxScroll = rail.scrollWidth - rail.clientWidth;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= maxScroll - 2;
      };

      var scrollBy = function (dir) {
        rail.scrollBy({
          left: dir * step(),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      };

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });

      rail.addEventListener('scroll', function () {
        window.requestAnimationFrame(sync);
      }, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot() {
    initHeroes();
    initReveal();
    initCarousels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
